import React from "react";
import { useTranslation } from "react-i18next";
import { Card, Row, Col } from "react-bootstrap";
import DashboardStatBox from "./DashboardStatBox";
import {
  DashboardHorizontalBarChart,
  DashboardDonutChart,
  DashboardComparisonChart,
  DashboardGenderSplitChart,
} from "./DashboardCharts";
import { formatHours, formatPercent } from "./dashboardStatisticsUtils";

const AdminDashboardView = ({ data }) => {
  const { t } = useTranslation();
  const d = data;
  const activeHeadcount = Math.max(0, d.totalUsers - d.peopleOnLeave);

  return (
    <>
      <Card className="shadow-sm border-0 mb-4 analytics-card">
        <Card.Header className="bg-light border-0 py-2 px-2 px-md-3">
          <span className="fw-semibold text-secondary small text-uppercase">
            <i className="bi bi-bar-chart-line me-2" />
            {t("app.analytics.periodMetrics")}
          </span>
        </Card.Header>
        <Card.Body className="pt-2 pb-3 px-2 px-md-3">
          <Row className="g-2 g-md-3">
            <DashboardStatBox value={d.peopleOnLeave} label={t("app.analytics.peopleOnLeave")} icon="bi-calendar-x-fill" iconColor="#E57373" background="rgba(229, 115, 115, 0.08)" />
            <DashboardStatBox value={d.peopleWorked} label={t("app.analytics.peopleWorked")} icon="bi-clock-history" iconColor="#4A90E2" background="rgba(74, 144, 226, 0.08)" />
            <DashboardStatBox value={formatHours(d.totalHoursWorked)} label={t("app.analytics.totalHoursWorked")} icon="bi-hourglass-split" iconColor="#FFA726" background="rgba(255, 167, 38, 0.08)" />
            <DashboardStatBox value={formatPercent(d.attendanceRatePercent)} label={t("app.analytics.attendanceRate")} icon="bi-person-check" iconColor="#66BB6A" background="rgba(102, 187, 106, 0.08)" />
            <DashboardStatBox value={d.totalTeams} label={t("app.analytics.totalTeams")} icon="bi-people-fill" iconColor="#4ECDC4" background="rgba(78, 205, 196, 0.08)" />
            <DashboardStatBox value={d.totalProjects} label={t("app.analytics.totalProjects")} icon="bi-kanban" iconColor="#BA68C8" background="rgba(186, 104, 200, 0.08)" />
            <DashboardStatBox value={d.totalEmploymentTypes} label={t("app.analytics.totalEmploymentTypes")} icon="bi-briefcase" iconColor="#4DD0E1" background="rgba(77, 208, 225, 0.08)" />
            <DashboardStatBox value={d.totalDesignations} label={t("app.analytics.totalDesignations")} icon="bi-award" iconColor="#FFD54F" background="rgba(255, 213, 79, 0.08)" />
            <DashboardStatBox value={d.totalDocumentsUploaded} label={t("app.analytics.documentsUploaded")} icon="bi-file-earmark-arrow-up" iconColor="#E57373" background="rgba(229, 115, 115, 0.08)" />
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col xs={12} lg={6}>
          <DashboardComparisonChart
            title={t("app.analytics.workforceActivity")}
            icon="bi-people"
            bars={[
              { key: "worked", label: t("app.analytics.peopleWorked"), value: d.peopleWorked, color: "#4A90E2" },
              { key: "leave", label: t("app.analytics.peopleOnLeave"), value: d.peopleOnLeave, color: "#E57373" },
              { key: "active", label: t("app.analytics.activeHeadcount"), value: activeHeadcount, color: "#66BB6A" },
            ]}
          />
        </Col>
        <Col xs={12} lg={6}>
          <DashboardComparisonChart
            title={t("app.analytics.masterDataOverview")}
            icon="bi-database"
            bars={[
              { key: "teams", label: t("app.analytics.totalTeams"), value: d.totalTeams, color: "#4ECDC4" },
              { key: "projects", label: t("app.analytics.totalProjects"), value: d.totalProjects, color: "#BA68C8" },
              { key: "designations", label: t("app.analytics.totalDesignations"), value: d.totalDesignations, color: "#FFD54F" },
            ]}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={6}>
          <DashboardHorizontalBarChart
            title={t("app.analytics.employmentTypeBreakdown")}
            icon="bi-briefcase"
            items={d.employmentTypeBreakdown}
          />
        </Col>
        <Col xs={12} lg={6}>
          <DashboardDonutChart
            title={t("app.analytics.employmentTypeDistribution")}
            icon="bi-pie-chart"
            items={d.employmentTypeBreakdown}
            centerLabel={t("app.analytics.employees")}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={6}>
          <DashboardDonutChart
            title={t("app.analytics.genderBreakdown")}
            icon="bi-gender-ambiguous"
            items={d.genderBreakdown}
            centerLabel={t("app.analytics.total")}
          />
        </Col>
        <Col xs={12} lg={6}>
          <DashboardGenderSplitChart genderRatio={d.genderRatio} genderBreakdown={d.genderBreakdown} />
        </Col>
      </Row>
    </>
  );
};

export default AdminDashboardView;
